import { useCallback, useEffect, useState } from 'react'
import { Coins, Loader2, Plus, Trash2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { formatCents } from '../../lib/money'
import {
  RateKindLabel,
  RateKindShort,
  type ClientRateDto,
  type ClientRateListResponse,
  type RateDto,
  type RateListResponse,
} from '@toga/shared'

interface Props {
  clientId: string
  canWrite: boolean
}

/**
 * Panel de tarifas asignadas a un cliente. Permite añadir una tarifa del
 * catálogo, opcionalmente con un importe override específico para ese cliente.
 * Se usa dentro del modal de edición de cliente.
 */
export default function ClientRatesPanel({ clientId, canWrite }: Props) {
  const [items, setItems] = useState<ClientRateDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { items } = await api.get<ClientRateListResponse>(`/clients/${clientId}/rates`)
      setItems(items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar')
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => {
    void reload()
  }, [reload])

  const onRemove = async (a: ClientRateDto) => {
    if (!confirm(`¿Quitar la asignación de "${a.rate.name}"?`)) return
    try {
      await api.delete(`/clients/${clientId}/rates/${a.id}`)
      await reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  return (
    <section className="rounded-lg border border-base p-3 space-y-3">
      <header className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider t-faint flex items-center gap-2">
          <Coins size={12} className="text-toga-300" /> Tarifas asignadas
        </h3>
        {canWrite && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-[11px] px-2 py-1 rounded border border-base t-secondary hover:bg-hover hover:text-toga-200 flex items-center gap-1"
          >
            <Plus size={11} /> Añadir
          </button>
        )}
      </header>

      {loading && (
        <div className="text-xs t-muted flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" /> Cargando…
        </div>
      )}

      {!loading && items.length === 0 && (
        <p className="text-xs t-faint">
          Sin tarifas asignadas. Este cliente facturará al precio base del catálogo.
        </p>
      )}

      {!loading && items.length > 0 && (
        <ul className="divide-y divide-base/40 text-sm">
          {items.map((a) => {
            const applied = a.overrideAmountCents ?? a.rate.amountCents
            const hasOverride = a.overrideAmountCents != null
            return (
              <li key={a.id} className="py-2 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="t-primary truncate">{a.rate.name}</div>
                  <div className="text-[11px] t-faint flex items-center gap-2 flex-wrap">
                    <span>{RateKindLabel[a.rate.kind]}</span>
                    <span>·</span>
                    <span className="font-mono">
                      {formatCents(applied)} {RateKindShort[a.rate.kind]}
                    </span>
                    {hasOverride && (
                      <span className="text-amber-300">
                        (base {formatCents(a.rate.amountCents)})
                      </span>
                    )}
                    {a.notes && <span className="t-muted">· {a.notes}</span>}
                  </div>
                </div>
                {canWrite && (
                  <button
                    type="button"
                    onClick={() => void onRemove(a)}
                    title="Quitar asignación"
                    className="w-7 h-7 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {error && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5">
          {error}
        </div>
      )}

      {adding && (
        <AddAssignmentInline
          clientId={clientId}
          assignedRateIds={new Set(items.map((i) => i.rateId))}
          onCancel={() => setAdding(false)}
          onSaved={async () => {
            setAdding(false)
            await reload()
          }}
        />
      )}
    </section>
  )
}

function AddAssignmentInline({
  clientId,
  assignedRateIds,
  onCancel,
  onSaved,
}: {
  clientId: string
  assignedRateIds: Set<string>
  onCancel: () => void
  onSaved: () => void
}) {
  const [catalog, setCatalog] = useState<RateDto[]>([])
  const [loading, setLoading] = useState(true)
  const [rateId, setRateId] = useState('')
  const [overrideEuros, setOverrideEuros] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<RateListResponse>('/rates?active=true')
      .then((r) => {
        const available = r.items.filter((it) => !assignedRateIds.has(it.id))
        setCatalog(available)
        if (available[0]) setRateId(available[0].id)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [assignedRateIds])

  const selected = catalog.find((c) => c.id === rateId) ?? null

  const onSubmit = async () => {
    if (!rateId) return
    setSubmitting(true)
    setError(null)
    try {
      const overrideNum = overrideEuros.trim()
        ? Number(overrideEuros.replace(',', '.'))
        : null
      if (overrideNum != null && (Number.isNaN(overrideNum) || overrideNum < 0)) {
        setSubmitting(false)
        return setError('Importe override no válido')
      }
      await api.post(`/clients/${clientId}/rates`, {
        rateId,
        overrideAmountCents:
          overrideNum != null ? Math.round(overrideNum * 100) : null,
        notes: notes.trim() || null,
      })
      onSaved()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? 'No se pudo asignar')
      } else {
        setError('Error de red')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="text-xs t-muted flex items-center gap-2 px-2 py-3">
        <Loader2 size={12} className="animate-spin" /> Cargando catálogo…
      </div>
    )
  }

  if (catalog.length === 0) {
    return (
      <div className="text-xs t-faint italic px-2 py-3 flex items-center justify-between">
        No hay tarifas activas sin asignar.
        <button
          type="button"
          onClick={onCancel}
          className="px-2 py-1 rounded text-xs t-secondary hover:bg-hover"
        >
          Cerrar
        </button>
      </div>
    )
  }

  return (
    <div className="rounded border border-toga-500/30 bg-slate-900/40 p-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <select
          value={rateId}
          onChange={(e) => setRateId(e.target.value)}
          className="bg-slate-900/60 border border-slate-700/60 rounded px-2 py-1.5 text-sm t-primary"
        >
          {catalog.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} — {formatCents(r.amountCents)} {RateKindShort[r.kind]}
            </option>
          ))}
        </select>
        <input
          inputMode="decimal"
          value={overrideEuros}
          onChange={(e) => setOverrideEuros(e.target.value)}
          placeholder={
            selected ? `Override (€), base ${(selected.amountCents / 100).toFixed(2)}` : 'Override'
          }
          className="bg-slate-900/60 border border-slate-700/60 rounded px-2 py-1.5 text-sm t-primary font-mono text-right"
        />
      </div>
      <input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notas (opcional)"
        className="w-full bg-slate-900/60 border border-slate-700/60 rounded px-2 py-1.5 text-sm t-primary"
      />
      {error && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1">
          {error}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 rounded text-xs t-secondary hover:bg-hover"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => void onSubmit()}
          disabled={submitting || !rateId}
          className="px-3 py-1 rounded text-xs font-semibold text-white bg-toga-600 hover:bg-toga-500 disabled:opacity-50 flex items-center gap-1"
        >
          {submitting && <Loader2 size={11} className="animate-spin" />}
          Asignar
        </button>
      </div>
    </div>
  )
}
